/**
 * Simulation Constants
 * Shared data for scenario types, job categories, and communication topics
 */

import {
    Users,
    Briefcase,
    MessageCircle,
    DollarSign,
    FileText,
    MessageSquare
} from 'lucide-react';

export const scenarioTypes = [
    {
        id: 'candidate_screening',
        title: 'Candidate Screening',
        description: 'Interview a potential candidate and assess their fit for the role',
        longDescription: 'Practice your interviewing skills by screening a software engineer candidate. Assess their technical skills, cultural fit, and career goals.',
        icon: Users,
        color: 'text-primary',
        bgColor: 'bg-primary/10',
        borderColor: 'border-primary/30',
        skills: ['Questioning techniques', 'Active listening', 'Assessment skills'],
        scenarioCount: '12 scenarios'
    },
    {
        id: 'client_intake',
        title: 'Client Intake Call',
        description: 'Handle a hiring manager intake conversation professionally',
        longDescription: 'Conduct an intake call with a startup hiring manager. Understand their needs, set expectations, and build a strong client relationship.',
        icon: Briefcase,
        color: 'text-secondary',
        bgColor: 'bg-secondary/10',
        borderColor: 'border-secondary/30',
        skills: ['Needs assessment', 'Expectation setting', 'Relationship building'],
        scenarioCount: '7 scenarios'
    },
    {
        id: 'objection_handling',
        title: 'Objection Handling',
        description: 'Address candidate concerns and overcome objections',
        longDescription: 'Handle a candidate who has concerns about the offered position. Practice addressing objections professionally and finding solutions.',
        icon: MessageCircle,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500/10',
        borderColor: 'border-yellow-500/30',
        skills: ['Active listening', 'Problem solving', 'Persuasion'],
        scenarioCount: '19 scenarios'
    },
    {
        id: 'salary_negotiation',
        title: 'Salary Negotiation',
        description: 'Navigate compensation discussions successfully',
        longDescription: 'Negotiate salary with a candidate who expects more than the offered amount. Find a win-win solution while maintaining budget constraints.',
        icon: DollarSign,
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/30',
        skills: ['Negotiation tactics', 'Value proposition', 'Closing skills'],
        scenarioCount: '12 scenarios'
    },
    {
        id: 'job_analysis',
        title: 'Job Analysis',
        description: 'Test your understanding of job descriptions',
        longDescription: 'Practice understanding and explaining job descriptions. An AI interviewer will test your knowledge of the role, responsibilities, skills, and qualifications.',
        icon: FileText,
        color: 'text-violet-500',
        bgColor: 'bg-violet-500/10',
        borderColor: 'border-violet-500/30',
        skills: ['Job understanding', 'Role clarity', 'Requirements knowledge', 'Communication'],
        scenarioCount: '4 job categories',
        isJobAnalysis: true
    },
    {
        id: 'communication',
        title: 'Communication Coach',
        description: 'Improve sentence formation, structure & professional communication',
        longDescription: 'Practice and master professional communication skills through structured exercises. Get real-time feedback on clarity, fluency, vocabulary, and confidence.',
        icon: MessageSquare,
        color: 'text-pink-500',
        bgColor: 'bg-pink-500/10',
        borderColor: 'border-pink-500/30',
        skills: ['Clarity', 'Fluency', 'Structure', 'Vocabulary', 'Confidence', 'Empathy'],
        scenarioCount: '18 practice topics',
        isCommunication: true
    }
];

export const jobCategories = [
    { id: 'it', name: 'IT & Technology', icon: '💻', description: 'Software, Data, Cloud, DevOps roles' },
    { id: 'professional', name: 'Professional Services', icon: '💼', description: 'Management, Finance, HR, Sales roles' },
    { id: 'admin_clerical', name: 'Administrative', icon: '📋', description: 'Admin, Clerical, Support roles' },
    { id: 'healthcare', name: 'Healthcare', icon: '🏥', description: 'Nursing, Clinical, Allied Health roles' }
];

export const communicationTopics = [
    { id: 'self_introduction', name: 'Self Introduction & Elevator Pitch', icon: '👋', description: 'Practice introducing yourself professionally' },
    { id: 'explain_complex', name: 'Explaining Complex Topics', icon: '🎯', description: 'Break down complex concepts simply' },
    { id: 'constructive_feedback', name: 'Giving Constructive Feedback', icon: '💡', description: 'Deliver feedback positively and actionably' },
    { id: 'difficult_conversations', name: 'Handling Difficult Conversations', icon: '⚡', description: 'Navigate challenging discussions professionally' },
    { id: 'active_listening', name: 'Active Listening & Paraphrasing', icon: '👂', description: 'Demonstrate understanding through reflection' },
    { id: 'persuasion', name: 'Persuasion & Negotiation', icon: '🤝', description: 'Influence decisions through compelling communication' },
    { id: 'sourcing_strategy', name: 'Sourcing Strategy Discussion', icon: '🔍', description: 'Explain and defend sourcing approaches' },
    { id: 'candidate_engagement', name: 'Candidate Engagement', icon: '💬', description: 'Build rapport and maintain relationships' },
    { id: 'offer_presentation', name: 'Presenting & Closing an Offer', icon: '📨', description: 'Present offers confidently and secure a yes' },
    { id: 'stakeholder_updates', name: 'Pipeline Updates to Leadership', icon: '📊', description: 'Give concise, data-driven hiring updates' },
    { id: 'inclusive_language', name: 'Inclusive Hiring Conversations', icon: '🌍', description: 'Use inclusive, bias-free language' },
    { id: 'cold_outreach', name: 'Cold Call & Voicemail Pitch', icon: '📞', description: "Grab a passive candidate's attention fast" },
    { id: 'disrespectful_language', name: 'Candidate Uses Disrespectful Language', icon: '🛑', description: 'Stay calm and set boundaries with a rude, angry candidate', roleplay: true },
    { id: 'interrupts_repeatedly', name: 'Candidate Interrupts Repeatedly', icon: '🗣️', description: 'Keep control when a candidate keeps talking over you', roleplay: true },
    { id: 'demands_immediate_answer', name: 'Candidate Demands an Immediate Answer', icon: '⏱️', description: "Stay honest under pressure for an answer you don't have yet", roleplay: true },
    { id: 'inappropriate_comments', name: 'Inappropriate Personal Comments', icon: '🚫', description: 'Redirect and set boundaries professionally', roleplay: true },
    { id: 'blames_recruiter', name: 'Candidate Blames You for No Updates', icon: '📵', description: 'Own the gap, rebuild trust and give a clear update', roleplay: true },
    { id: 'call_closing', name: 'Call Closing: Further Help & Direct Number', icon: '📲', description: 'Close with next steps, an offer of help and your direct line', roleplay: true }
];
